import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  IconButton,
  Chip,
  Collapse,
  Divider,
  Button,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Dashboard as DashboardIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  People as PeopleIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import { THEME_COLORS } from '../../constants';
import { formatCelular } from '../../utils';

/**
 * Componente de tarjeta de distribuidor para vista móvil
 */
const DistributorCard = ({
  distributor,
  isExpanded,
  onExpand,
  onEdit,
  onDelete,
  onToggleStatus,
  onViewDashboard,
  showActions = true,
}) => {
  return (
    <Card
      sx={{
        marginBottom: 2,
        border: `1px solid ${THEME_COLORS.BORDER}`,
        borderRadius: '12px',
        overflow: 'hidden',
        backgroundColor: THEME_COLORS.WHITE,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          borderColor: THEME_COLORS.BORDER_HOVER,
          transform: 'translateY(-1px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <CardContent sx={{ padding: 3 }}>
        {/* Header con info básica */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant="h6"
                sx={{
                  color: THEME_COLORS.TEXT_PRIMARY,
                  fontWeight: 600,
                  fontSize: '1.1rem',
                  marginBottom: 0.5,
                }}
              >
                {distributor.nombre_empresa}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, marginBottom: 0.5 }}>
                <PersonIcon sx={{ fontSize: '0.9rem', color: THEME_COLORS.SECONDARY }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: THEME_COLORS.TEXT_SECONDARY,
                    fontSize: '0.875rem',
                  }}
                >
                  {distributor.nombre_distribuidor}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon sx={{ fontSize: '0.9rem', color: THEME_COLORS.SECONDARY }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: THEME_COLORS.TEXT_SECONDARY,
                    fontSize: '0.875rem',
                  }}
                >
                  {formatCelular(distributor.celular)}
                </Typography>
                {/* Indicador de estado */}
                {distributor.is_active ? (
                  <CheckCircleIcon sx={{ fontSize: '0.8rem', color: THEME_COLORS.TEXT_SECONDARY }} />
                ) : (
                  <CancelIcon sx={{ fontSize: '0.8rem', color: THEME_COLORS.TEXT_MUTED }} />
                )}
              </Box>
            </Box>
          </Box>

          {showActions && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <IconButton 
                size="small" 
                sx={{ color: THEME_COLORS.SECONDARY }}
                onClick={() => onViewDashboard(distributor)}
              >
                <DashboardIcon fontSize="small" />
              </IconButton>
              <IconButton 
                size="small" 
                sx={{ color: THEME_COLORS.SECONDARY }}
                onClick={() => onEdit(distributor)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Información básica */}
        <Box sx={{ marginBottom: 2 }}>
          {/* Email eWeLink */}
          <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
            <EmailIcon sx={{ fontSize: '0.9rem', color: THEME_COLORS.SECONDARY, marginRight: 1 }} />
            <Typography
              variant="body2"
              sx={{
                color: THEME_COLORS.TEXT_SECONDARY,
                fontSize: '0.875rem',
              }}
            >
              {distributor.cuenta_ewelink}
            </Typography>
          </Box>

          {/* Ubicación */}
          {(distributor.ciudad || distributor.estado) && (
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
              <LocationIcon sx={{ fontSize: '0.9rem', color: THEME_COLORS.SECONDARY, marginRight: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  color: THEME_COLORS.TEXT_SECONDARY,
                  fontSize: '0.875rem',
                }}
              >
                {distributor.ciudad ? `${distributor.ciudad}${distributor.estado ? `, ${distributor.estado}` : ''}` : distributor.estado}
              </Typography>
            </Box>
          )}

          {/* Cuenta bancaria */}
          {distributor.numero_cuenta_bancaria && (
            <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 1 }}>
              <AccountBalanceIcon sx={{ fontSize: '0.9rem', color: THEME_COLORS.SECONDARY, marginRight: 1 }} />
              <Typography
                variant="body2"
                sx={{
                  color: THEME_COLORS.TEXT_SECONDARY,
                  fontSize: '0.875rem',
                }}
              >
                {distributor.numero_cuenta_bancaria}
              </Typography>
            </Box>
          )}

          {/* Estadísticas como chips */}
          <Box sx={{ display: 'flex', gap: 1, marginTop: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<PeopleIcon />}
              label={`${distributor.total_contacts || 0} contactos`}
              size="small"
              sx={{
                fontSize: '0.75rem',
                height: 24,
                backgroundColor: distributor.total_contacts > 0 ? THEME_COLORS.TEXT_PRIMARY : THEME_COLORS.TEXT_MUTED,
                color: 'white',
                '& .MuiChip-icon': { color: 'white', fontSize: '14px' },
              }}
            />
            <Chip
              icon={<CheckCircleIcon />}
              label={`${distributor.active_subscriptions || 0} activas`}
              size="small"
              sx={{
                fontSize: '0.75rem',
                height: 24,
                backgroundColor: THEME_COLORS.TEXT_SECONDARY,
                color: 'white',
                '& .MuiChip-icon': { color: 'white', fontSize: '14px' },
              }}
            />
            <Chip
              icon={<AttachMoneyIcon />}
              label={`$${(distributor.total_revenue || 0).toLocaleString('es-CO')}`}
              size="small"
              sx={{
                fontSize: '0.75rem',
                height: 24,
                backgroundColor: THEME_COLORS.SECONDARY,
                color: 'white',
                '& .MuiChip-icon': { color: 'white', fontSize: '14px' },
              }}
            />
          </Box>
        </Box>

        {/* Estado y acciones adicionales */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
          <Chip
            icon={distributor.is_active ? <CheckCircleIcon /> : <CancelIcon />}
            label={distributor.is_active ? 'Activo' : 'Inactivo'}
            size="small"
            sx={{
              backgroundColor: distributor.is_active ? THEME_COLORS.TEXT_SECONDARY : THEME_COLORS.TEXT_MUTED,
              color: 'white',
              fontSize: '0.75rem',
              fontWeight: 600,
              '& .MuiChip-icon': { color: 'white', fontSize: '14px' },
            }}
          />

          {showActions && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={distributor.is_active ? <ToggleOffIcon /> : <ToggleOnIcon />}
                onClick={() => onToggleStatus(distributor)}
                sx={{
                  fontSize: '0.7rem',
                  padding: '2px 8px',
                  minWidth: 'auto',
                  borderColor: distributor.is_active ? THEME_COLORS.TEXT_MUTED : THEME_COLORS.TEXT_SECONDARY,
                  color: distributor.is_active ? THEME_COLORS.TEXT_MUTED : THEME_COLORS.TEXT_SECONDARY,
                  '&:hover': {
                    borderColor: distributor.is_active ? THEME_COLORS.SECONDARY : THEME_COLORS.TEXT_PRIMARY,
                    backgroundColor: distributor.is_active ? THEME_COLORS.LIGHT_GRAY : THEME_COLORS.BACKGROUND_PAPER,
                  },
                }}
              >
                {distributor.is_active ? 'Desactivar' : 'Activar'}
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={() => onDelete(distributor)}
                sx={{
                  fontSize: '0.7rem',
                  padding: '2px 8px',
                  minWidth: 'auto',
                  borderColor: THEME_COLORS.ERROR,
                  color: THEME_COLORS.ERROR,
                  '&:hover': {
                    borderColor: THEME_COLORS.SECONDARY,
                    backgroundColor: THEME_COLORS.LIGHT_GRAY,
                  },
                }}
              >
                Eliminar
              </Button>
            </Box>
          )}
        </Box>

        {/* Botón expandir/contraer */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            paddingTop: 1,
            borderTop: `1px solid ${THEME_COLORS.BORDER}`,
          }}
        >
          <IconButton
            size="small"
            onClick={() => onExpand(distributor.distributor_id)}
            sx={{
              color: THEME_COLORS.SECONDARY,
              fontSize: '0.875rem',
            }}
          >
            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            <Typography
              variant="body2"
              sx={{
                marginLeft: 0.5,
                fontSize: '0.75rem',
                color: THEME_COLORS.TEXT_MUTED,
              }}
            >
              {isExpanded ? 'Menos detalles' : 'Más detalles'}
            </Typography>
          </IconButton>
        </Box>

        {/* Sección expandible de detalles */}
        <Collapse in={isExpanded}>
          <Divider sx={{ marginY: 2 }} />
          <Box sx={{ paddingTop: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{
                color: THEME_COLORS.TEXT_PRIMARY,
                fontWeight: 600,
                marginBottom: 2,
                fontSize: '0.875rem',
              }}
            >
              Detalles del Distribuidor
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_MUTED, fontSize: '0.8rem' }}>
                  ID:
                </Typography>
                <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY, fontSize: '0.8rem' }}>
                  {distributor.distributor_id.slice(0, 8)}...
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_MUTED, fontSize: '0.8rem' }}>
                  Empresa:
                </Typography>
                <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY, fontSize: '0.8rem' }}>
                  {distributor.nombre_empresa}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_MUTED, fontSize: '0.8rem' }}>
                  Distribuidor:
                </Typography>
                <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY, fontSize: '0.8rem' }}>
                  {distributor.nombre_distribuidor}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_MUTED, fontSize: '0.8rem' }}>
                  Creado:
                </Typography>
                <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY, fontSize: '0.8rem' }}>
                  {new Date(distributor.created_at).toLocaleDateString()}
                </Typography>
              </Box>

              {distributor.updated_at && distributor.updated_at !== distributor.created_at && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_MUTED, fontSize: '0.8rem' }}>
                    Actualizado:
                  </Typography>
                  <Typography variant="body2" sx={{ color: THEME_COLORS.TEXT_SECONDARY, fontSize: '0.8rem' }}>
                    {new Date(distributor.updated_at).toLocaleDateString()}
                  </Typography>
                </Box>
              )}

              {/* Botón para ver dashboard completo */}
              <Box sx={{ marginTop: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<DashboardIcon />}
                  onClick={() => onViewDashboard(distributor)}
                  sx={{
                    backgroundColor: THEME_COLORS.TEXT_PRIMARY,
                    color: THEME_COLORS.BACKGROUND_PAPER,
                    borderRadius: '8px',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    '&:hover': { backgroundColor: THEME_COLORS.TEXT_PRIMARY },
                  }}
                >
                  Ver Dashboard Completo
                </Button>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default DistributorCard;